﻿import { useEffect, useState } from 'react';
import useGameStore, { advisorFor, districtRentFor, ownershipPct, pricePerShareOf, rentYieldOf } from '../store/gameStore';
import type { PropertyTile } from '../types/gameTypes';
import { money } from '../lib/format';
import { useT } from '../i18n';
import { DISTRICT_META } from './districtMeta';

// Pip layout of a d6 rendered on a 3x3 grid.
const PIP_POSITIONS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const Die = ({ value, rolling }: { value: number; rolling: boolean }) => (
  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-300/70 bg-slate-100 text-slate-900 shadow-[0_4px_10px_rgba(0,0,0,0.45)] ${rolling ? 'die-roll' : ''}`}>
    {value > 0 && value <= 6 ? (
      <span className="grid h-8 w-8 grid-cols-3 grid-rows-3">
        {Array.from({ length: 9 }).map((_, cell) => (
          <span key={cell} className="flex items-center justify-center">
            {PIP_POSITIONS[value]?.includes(cell) ? <span className="h-1.5 w-1.5 rounded-full bg-slate-900" /> : null}
          </span>
        ))}
      </span>
    ) : value > 6 ? (
      <span className="text-lg font-bold text-slate-900">{value}</span>
    ) : (
      <span className="text-lg font-semibold text-slate-400">–</span>
    )}
  </div>
);

/** Two preset packages only: 50 % control or the whole plot. No 1–100 sliders. */
const presetPackagesOf = (tile: PropertyTile, humanId: string, cash: number) => {
  const held = Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
  const float = Math.max(0, tile.totalShares - held);
  const unit = Math.max(1, pricePerShareOf(tile));
  const owned = tile.shares[humanId] ?? 0;
  const mk = (shares: number) => {
    const capped = Math.max(0, Math.min(float, Math.ceil(shares)));
    const cost = Math.round(capped * unit);
    return { shares: capped, cost, affordable: cash >= cost };
  };
  return {
    control: mk(tile.totalShares / 2 - owned + 0.01),
    whole: mk(tile.totalShares - owned),
    float,
  };
};

/**
 * THE decision card: exactly one situation in plain language + the single next action.
 * Secondary mechanics (loans, insurance, voting) live in a collapsed drawer.
 */
const ActionSteps = () => {
  const store = useGameStore();
  const t = useT();
  const {
    board,
    players,
    market,
    currentPlayerIndex,
    turnPhase,
    movementPoints,
    selectedTileIndex,
    auction,
    proposal,
    gameOver,
    pendingOffer,
  } = store;

  const [diceValues, setDiceValues] = useState<[number, number]>([0, 0]);
  const [rolling, setRolling] = useState(false);

  const active = players[currentPlayerIndex];
  const human = players.find((p) => !p.isAI) ?? players[0];
  const isHumanTurn = Boolean(active && !active.isAI);

  // Animate both dice whenever a fresh roll lands (human or bot).
  useEffect(() => {
    if (movementPoints <= 0) return;
    setDiceValues(store.dice ?? [0, 0]);
    setRolling(true);
    const timer = window.setTimeout(() => setRolling(false), 420);
    return () => window.clearTimeout(timer);
  }, [movementPoints, store.dice]);

  const rollDie = () => {
    if (rolling) return;
    setRolling(true);
    const spin = window.setInterval(() => {
      setDiceValues([1 + Math.floor(Math.random() * 6), 1 + Math.floor(Math.random() * 6)]);
    }, 70);
    window.setTimeout(() => {
      window.clearInterval(spin);
      setRolling(false);
      // The store rolls two dice and moves the pawn automatically.
      store.rollMovementPoints(6);
    }, 420);
  };

  const landingIndex = selectedTileIndex ?? active?.currentTileIndex ?? null;
  const landingTile: PropertyTile | null = landingIndex !== null ? board[landingIndex] ?? null : null;
  const offerOpen = Boolean(isHumanTurn && pendingOffer && landingTile);
  const ownsPct = human && landingTile ? ownershipPct(landingTile, human.id) : 0;
  const rivalEntry = landingTile
    ? Object.entries(landingTile.shares).filter(([id]) => id !== (human?.id ?? '')).sort((a, b) => b[1] - a[1])[0]
    : null;
  const controllingRival =
    landingTile && rivalEntry && rivalEntry[1] > landingTile.totalShares / 2 ? players.find((p) => p.id === rivalEntry[0]) : null;
  const rentDue =
    landingTile && landingTile.tileType === 'COMMERCIAL' && !landingTile.isFrozen
      ? Math.max(0, Math.round((districtRentFor(landingTile, market.phase, board) * (100 - Math.min(100, ownsPct))) / 100))
      : 0;
  const hint = isHumanTurn && human ? advisorFor(board, human, landingTile) : null;
  const pkg = landingTile && human ? presetPackagesOf(landingTile, human.id, human.cash) : null;

  const isRollStep = isHumanTurn && turnPhase === 'TURN_START' && !auction.isOpen && !pendingOffer;
  const isSettleStep =
    isHumanTurn && !offerOpen && !isRollStep && !auction.isOpen && (turnPhase === 'TILE_RESOLUTION' || turnPhase === 'TURN_END');
  const governStep = isHumanTurn && turnPhase === 'GOVERNANCE_PHASE' && !auction.isOpen;
  const voting = proposal.status === 'PENDING' && Boolean(proposal.propertyId);

  const headline = (() => {
    if (gameOver) return 'Hra skončila — podívej se na konečné pořadí.';
    if (auction.isOpen) return 'Běží aukce — přihodit, nebo se vzdát?';
    if (!isHumanTurn) return `${active?.name ?? 'Protivník'} hraje…`;
    if (offerOpen && landingTile) return `Stoupl jsi na ${landingTile.name}. Nikdo ho nevlastní.`;
    if (governStep) return `${landingTile?.name ?? 'Přepážka'} — vyber, co podnikneš.`;
    if (isRollStep) return 'Jsi na tahu — hoď dvěma kostkami. Půjdeš přesně tam, kam padne součet.';
    if (isSettleStep && landingTile && controllingRival && rentDue > 0) return `Stoupl jsi na území ${controllingRival.name}. Platíš nájem ${money(rentDue)}.`;
    if (isSettleStep && landingTile) return `${landingTile.name} vyřízeno — předej kostku dál.`;
    if (voting) return 'Běží hlasování o kapitálové výstavbě.';
    return `${active?.name ?? ''} · ${turnPhase}`;
  })();

  const cta = 'min-h-[56px] w-full rounded-2xl px-5 py-3 text-base font-black transition disabled:cursor-not-allowed disabled:opacity-40';
  const primary = `${cta} border-2 border-emerald-300 bg-gradient-to-r from-emerald-400 to-teal-300 text-slate-950 hover:from-emerald-300 hover:to-teal-200`;
  const buyCta = `${cta} border-2 border-cyan-300 bg-gradient-to-r from-cyan-400 to-sky-400 text-slate-950 hover:from-cyan-300 hover:to-sky-300`;
  const auctionCta = `${cta} border-2 border-amber-300 bg-gradient-to-r from-amber-300 to-orange-400 text-slate-950 hover:from-amber-200 hover:to-orange-300`;
  const neutral = `${cta} border-2 border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700`;

  return (
    <div className="rounded-2xl border border-cyan-500/30 bg-slate-900/80 p-4 shadow-lg">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          <Die value={diceValues[0]} rolling={rolling} />
          <Die value={diceValues[1]} rolling={rolling} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-300">{t('common.round')} {market.currentRound}</p>
          <h2 className="text-base font-extrabold leading-snug text-white">{headline}</h2>
        </div>
      </div>

      {hint && (
        <p className="mt-2 rounded-xl border border-sky-400/40 bg-sky-500/10 px-3 py-2 text-xs font-bold text-sky-200">{hint}</p>
      )}

      {landingTile && landingTile.tileType === 'COMMERCIAL' && isHumanTurn && (
        <p className="mt-2 text-[11px] font-black" style={{ color: DISTRICT_META[landingTile.district].bar }}>
          {t('action.rentDue', {
            district: DISTRICT_META[landingTile.district].label,
            pct: ownsPct.toFixed(1),
            rent: money(rentDue),
            yield: `${(rentYieldOf(landingTile) * 100).toFixed(1)} %`,
          })}
        </p>
      )}

      <div className="mt-3 grid gap-2">
        {/* 1) Unowned landing: two preset packages + auction. No sliders. */}
        {offerOpen && landingTile && pkg && human && (
          <>
            <button
              type="button"
              disabled={pkg.control.shares <= 0 || !pkg.control.affordable}
              onClick={() => { store.addShares(human.id, landingTile.id, pkg.control.shares, pricePerShareOf(landingTile)); store.clearOffer(); }}
              className={buyCta}
            >
              {t('action.buyDirect')} 50 % ({pkg.control.shares}) · {money(pkg.control.cost)}
            </button>
            {pkg.whole.shares > pkg.control.shares && (
              <button
                type="button"
                disabled={!pkg.whole.affordable}
                onClick={() => { store.addShares(human.id, landingTile.id, pkg.whole.shares, pricePerShareOf(landingTile)); store.clearOffer(); }}
                className={buyCta}
              >
                {t('action.buyDirect')} 100 % ({pkg.whole.shares}) · {money(pkg.whole.cost)}
              </button>
            )}
            <button
              type="button"
              disabled={auction.isOpen}
              onClick={() => { store.clearOffer(); store.openAuction(landingTile.id, pkg.float); }}
              className={auctionCta}
            >
              {t('action.auction')} ($50)
            </button>
          </>
        )}

        {/* 2) Roll the die. */}
        {isRollStep && (
          <button type="button" disabled={rolling} onClick={rollDie} className={primary}>{t('action.rollDice')}</button>
        )}

        {/* 3) Movement is automatic: the two-dice total decides the destination. */}
        {isRollStep && (
          <button type="button" disabled={rolling} onClick={rollDie} className={primary}>{t('action.rollDice')}</button>
        )}

        {/* 4) Settled tile: one continue button (rent already settled pro-rata). */}
        {isSettleStep && (
          <button type="button" onClick={() => store.finishTurn()} className={primary}>
            {t('action.payContinue')}
          </button>
        )}

        {/* 5) Financial desk / governance: optional drawer + leave. */}
        {governStep && (
          <>
            <button
              type="button"
              disabled={!human || !isHumanTurn}
              onClick={() => store.openFinancialDesk('LOANS')}
              className="min-h-[48px] w-full rounded-xl border-2 border-amber-300/80 bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-black text-slate-950 transition hover:from-amber-300 hover:to-orange-300 disabled:border-slate-700 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
            >
              {t('action.financialDesk')}
            </button>
            {human && (
              <p className="rounded-xl border border-slate-700/60 bg-slate-950/60 px-2.5 py-1.5 text-[11px] text-slate-400">
                {t('action.treasury', {
                  loans: money(store.loanExposureOf(human.id)),
                  investments: money(store.investmentValueOf(human.id)),
                  rate: market.phase === 'BOOM' ? '4 %' : market.phase === 'CRISIS' ? '12 %' : '7 %',
                })}
              </p>
            )}
            <details className="rounded-xl border border-slate-700/60 bg-slate-950/60 p-3">
              <summary className="cursor-pointer text-xs font-bold text-slate-300">{t('action.drawer')}</summary>
              <div className="mt-2 grid gap-2">
                <button
                  type="button"
                  disabled={!human || human.insuranceTurnsRemaining > 0 || human.cash < Math.round(store.netWorthOf(human?.id ?? '') * 0.05)}
                  onClick={() => human && store.buyInsurance(human.id, 'STANDARD')}
                  className={neutral}
                >
                  {t('action.insurance', { cost: money(Math.round(store.netWorthOf(human?.id ?? '') * 0.05)) })}
                </button>
              </div>
            </details>
            <button type="button" onClick={() => store.finishTurn()} className={primary}>{t('action.endTurn')}</button>
          </>
        )}

        {/* 6) Voting on a capital-build proposal. */}
        {voting && human && (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={proposal.votedPlayerIds.includes(human.id)} onClick={() => store.voteOnProposal(human.id, 'FOR')} className={primary}>{t('action.voteFor')}</button>
            <button type="button" disabled={proposal.votedPlayerIds.includes(human.id)} onClick={() => store.voteOnProposal(human.id, 'AGAINST')} className={neutral}>{t('action.voteAgainst')}</button>
          </div>
        )}

        {/* 6b) Rejected vote: the proposer may fund the whole bill alone. */}
        {!voting && proposal.status === 'REJECTED' && proposal.propertyId && human && proposal.proposerId === human.id && (
          <button
            type="button"
            disabled={human.cash < proposal.totalCost}
            onClick={() => store.selfFundUpgrade(proposal.propertyId)}
            className={auctionCta}
          >
            Zaplatit celé sám ({money(proposal.totalCost)}) — bez ředění, výhoda pro všechny
          </button>
        )}

        {/* 7) End turn — the single closing action. Always available on the human's turn;
            if an auction is somehow still open, finishing the turn settles it first. */}
        {isHumanTurn && !isRollStep && !offerOpen && !governStep && (
          <button type="button" disabled={gameOver} onClick={() => store.finishTurn()} className={primary}>
            {auction.isOpen ? `${t('action.endTurn')} · ${t('action.closeAuction')}` : t('action.endTurn')}
          </button>
        )}

        {!isHumanTurn && !gameOver && (
          <p className="text-center text-[11px] font-semibold text-slate-400">{t('action.opponentsAuto')}</p>
        )}
      </div>
    </div>
  );
};

export default ActionSteps;